import { object, string, z } from 'zod'

const AddressSchema = z.object({
  street_address: z.string(),
  street_address2: z.string(),
  postal_code: z.string(),
  city: z.string(),
  region: z.string(),
  country: z.string(),
})

const PersonSchema = z.object({
  title: z.string(),
  given_name: z.string(),
  family_name: z.string(),
})

const InsuranceSchema = z.object({
  insurance_company: z.string().nullable(),
  insurance_type: z.string().nullable(),
  insurance_price: z.number().nullable(),
})

const TravelItinerarySchema = z.object({
  departure: z.object({
    departure_airport: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
  }),
  arrival: z.object({
    arrival_airport: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
  }),
  carrier: z.string().nullable(),
  segment_price: z.number().nullable(),
  departure_date: z.string().nullable(),
  passenger_reference: z.array(z.string()),
  class: z.string().nullable(),
})

const CarRentalSchema = z.object({
  rental_company: z.string().nullable(),
  drivers_reference: z.array(z.number()),
  pick_up_location: AddressSchema.nullable(),
  start_time: z.string().nullable(),
  drop_off_location: AddressSchema.nullable(),
  end_time: z.string().nullable(),
  car_price: z.number().nullable(),
  class: z.string().nullable(),
})

const HotelReservationSchema = z.object({
  hotel_name: z.string().nullable(),
  address: AddressSchema.nullable(),
  start_time: z.string().nullable(),
  end_time: z.string().nullable(),
  number_of_rooms: z.number().nullable(),
  guest_reference: z.array(z.number()),
  hotel_price: z.number().nullable(),
  room_type: z.string().nullable(),
})

const EventTicketSchema = z.object({
  event_name: z.string(),
  event_company: z.string(),
  genre_of_event: z.string(),
  arena_name: z.string(),
  arena_location: AddressSchema.nullable(),
  start_time: z.string(),
  end_time: z.string(),
  access_controlled_venue: z.boolean(),
  affiliate_name: z.string(),
})

const VoucherDetailsSchema = z.object({
  voucher_name: z.string(),
  voucher_company: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  affiliate_name: z.string(),
})

const LineItemSchema = z.object({
  name: z.string(),
  quantity: z.number(),
  total_amount: z.number(),
  total_tax_amount: z.number(),
  unit_price: z.number(),
  product_url: z.string(),
  image_url: z.string(),
  product_identifier: z.string(),
  reference: z.string(),
  shipping_reference: z.string(),
  supplementary_details: z
    .object({
      travel_reservation_details: z
        .object({
          travel_type: z.string(),
          itinerary: z.array(TravelItinerarySchema),
          passengers: z.array(PersonSchema),
          insurance: z.array(InsuranceSchema),
          affiliate_name: z.string(),
        })
        .optional(),
      car_rental_details: z
        .object({
          car_rental_itinerary: z.array(CarRentalSchema),
          drivers: z.array(
            PersonSchema.extend({
              driver_reference: z.number(),
              date_of_birth: z.string(),
            })
          ),
          affiliate_name: z.string(),
          insurance: InsuranceSchema,
        })
        .optional(),
      hotel_reservation_details: z
        .object({
          hotel_reservation_itinerary: z.array(HotelReservationSchema),
          guests: z.array(
            PersonSchema.extend({
              guest_reference: z.number(),
            })
          ),
          affiliate_name: z.string(),
          insurance: InsuranceSchema,
        })
        .optional(),
      event_tickets_details: z.array(EventTicketSchema).optional(),
      insurance_details: InsuranceSchema.optional(),
      voucher_details: VoucherDetailsSchema.optional(),
    })
    .optional(),
})

export const ConsumerSessionData = z.object({
  supplementary_purchase_data: z.object({
    merchant_references: z.array(z.string()).optional(),
    line_items: z.array(LineItemSchema).optional(),
    shipping: z.object({}).optional(),
    buyer: z.object({}).optional(),
    subscriptions: z.object({}).optional(),
    ondemand: z.object({}).optional(),
    marketplace: z.object({}).optional(),
  }),
})

export const ConsumerSessionDataSchema = z.object({
  consumer_session_id: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
})

export const ConsumerApiBridgeSchema = {
  consumerApiCreateSession: {
    data: object({
      body: ConsumerSessionData,
      region: string(),
    }),
    response: ConsumerSessionDataSchema,
  },
  consumerApiGetSession: {
    data: object({
      consumerSessionId: string(),
      region: string(),
    }),
    response: ConsumerSessionDataSchema,
  },
  consumerApiUpdateSession: {
    data: object({
      consumerSessionId: string(),
      body: ConsumerSessionData,
      region: string(),
    }),
    response: ConsumerSessionDataSchema,
  },
}
